import moment from "moment";
import { listCalendarWeekPeriods } from "../../../../../../utils/block-review-helpers";
import { groupExercisesByComplex } from "../../../../../../utils/complex";
import {
  buildDateRangeInsightsInput,
  dateRangeInsightsStorageId,
} from "./buildDateRangeInsightsInput";

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Visible month grid bounds (matches calendar-month.controller + CalendarMonth UI).
 * @param {import("moment").Moment|string} activeMonth
 * @param {number} [startingCalendarDay]
 */
export function getCalendarMonthGridBounds(activeMonth, startingCalendarDay = 0) {
  const month = moment.isMoment(activeMonth)
    ? activeMonth.clone()
    : moment.utc(activeMonth);

  const startDay = startingCalendarDay || 0;
  let startOfFirstWeek = month.clone().startOf("week").weekday(startDay);
  const dayNumber = Number.parseInt(startOfFirstWeek.format("D"), 10);

  if (dayNumber > 1 && dayNumber < 7) {
    startOfFirstWeek = startOfFirstWeek.subtract(1, "week");
  }

  const potentialWeek5Start = startOfFirstWeek.clone().add(5, "weeks");
  const showWeek5 = potentialWeek5Start.month() === month.month();
  const endOfLastWeek = startOfFirstWeek
    .clone()
    .add(showWeek5 ? 6 : 5, "weeks")
    .subtract(1, "day")
    .endOf("day");

  return { startOfFirstWeek, endOfLastWeek, showWeek5 };
}

/**
 * @param {import("moment").Moment|string} activeMonth
 * @param {number} [startingCalendarDay]
 */
export function getCalendarMonthVisibleRange(activeMonth, startingCalendarDay = 0) {
  const { startOfFirstWeek, endOfLastWeek } = getCalendarMonthGridBounds(
    activeMonth,
    startingCalendarDay
  );

  return {
    from: startOfFirstWeek.format(DATE_FORMAT),
    to: endOfLastWeek.format(DATE_FORMAT),
  };
}

/**
 * Calendar week columns shown in month view (5 or 6), with display labels.
 * @param {import("moment").Moment|string} activeMonth
 * @param {number} [startingCalendarDay]
 */
export function listCalendarMonthVisiblePeriods(
  activeMonth,
  startingCalendarDay = 0
) {
  const { from, to } = getCalendarMonthVisibleRange(
    activeMonth,
    startingCalendarDay
  );
  const { showWeek5 } = getCalendarMonthGridBounds(activeMonth, startingCalendarDay);
  const periods = listCalendarWeekPeriods(from, to, startingCalendarDay);

  return showWeek5 ? periods : periods.slice(0, 5);
}

/**
 * Range + visible week columns for month insights (pure, cheap to call in render).
 * @param {import("moment").Moment|string} activeMonth
 * @param {number} [startingCalendarDay]
 */
export function getCalendarMonthInsightsRange(activeMonth, startingCalendarDay = 0) {
  const { from, to } = getCalendarMonthVisibleRange(
    activeMonth,
    startingCalendarDay
  );
  const visiblePeriods = listCalendarMonthVisiblePeriods(
    activeMonth,
    startingCalendarDay
  );

  return { from, to, visiblePeriods };
}

/**
 * @param {import("moment").Moment} activeWeek — week start (athlete starting day)
 * @param {number} [startingCalendarDay]
 */
export function getCalendarWeekVisibleRange(activeWeek, startingCalendarDay = 0) {
  const week = moment.isMoment(activeWeek)
    ? activeWeek.clone()
    : moment(activeWeek, DATE_FORMAT);

  const from = week.format(DATE_FORMAT);
  const to = week.clone().add(6, "days").format(DATE_FORMAT);

  return { from, to };
}

/**
 * Range + columns for one or more visible calendar weeks (week view).
 * @param {import("moment").Moment[]} weekStarts
 * @param {number} [startingCalendarDay]
 */
export function getCalendarWeeksInsightsRange(weekStarts, startingCalendarDay = 0) {
  if (!Array.isArray(weekStarts) || weekStarts.length === 0) {
    return null;
  }

  const sorted = [...weekStarts].sort((a, b) => a.valueOf() - b.valueOf());
  const from = sorted[0].format(DATE_FORMAT);
  const to = sorted[sorted.length - 1].clone().add(6, "days").format(DATE_FORMAT);
  const allPeriods = listCalendarWeekPeriods(from, to, startingCalendarDay);
  const visibleWeekStarts = new Set(
    sorted.map((week) => week.format(DATE_FORMAT))
  );

  const visiblePeriods = allPeriods.filter((period) => {
    const periodStart = period.periodKey.split(" - ")[0];
    return visibleWeekStarts.has(periodStart);
  });

  return { from, to, visiblePeriods };
}

/**
 * Exercises scheduled in a date range (same source as calendar cells).
 * @param {Object[]} activeExercises
 * @param {{ from: string, to: string }} range
 * @param {{ groupComplexes?: boolean }} [options]
 */
export function getCalendarInsightsExercises(
  activeExercises,
  { from, to },
  { groupComplexes = true } = {}
) {
  if (!Array.isArray(activeExercises) || !from || !to) {
    return [];
  }

  const rangeStart = moment.utc(from, DATE_FORMAT).startOf("day");
  const rangeEnd = moment.utc(to, DATE_FORMAT).endOf("day");

  const inRange = activeExercises.filter((exercise) => {
    if (exercise?.utcDate == null && exercise?.date == null) {
      return false;
    }
    const date = moment.utc(
      exercise.utcDate?.toString() || exercise.date
    );
    return date.isBetween(rangeStart, rangeEnd, null, "[]");
  });

  const sorted = [...inRange].sort((a, b) => {
    const aDate = Number(a.utcDate) || 0;
    const bDate = Number(b.utcDate) || 0;
    return aDate - bDate;
  });

  return groupComplexes ? groupExercisesByComplex(sorted) : sorted;
}

/**
 * Shared builder: map visible calendar week columns → Program Insights input.
 * @param {Object} params
 * @param {Object[]} params.exercises
 * @param {string} params.from
 * @param {string} params.to
 * @param {number} [params.startingCalendarDay]
 * @param {{ periodKey: string, label: string }[]} params.visiblePeriods
 */
export function buildCalendarRangeInsightsInput({
  exercises = [],
  from,
  to,
  startingCalendarDay = 0,
  visiblePeriods = [],
}) {
  if (!from || !to || visiblePeriods.length === 0) {
    return null;
  }

  const baseInput = buildDateRangeInsightsInput({
    exercises,
    from,
    to,
    startingCalendarDay,
  });

  if (!baseInput) {
    return null;
  }

  const builderWeeks = {};
  const weekHeaderLabels = {};

  visiblePeriods.forEach((period, index) => {
    const weekIndex = index + 1;
    const weekKey = String(weekIndex);
    weekHeaderLabels[weekIndex] = period.label;

    const sourceWeek = baseInput.builderWeeks[weekKey];
    builderWeeks[weekKey] = sourceWeek?.length ? sourceWeek : [];
  });

  return {
    builderWeeks,
    programExercises: baseInput.programExercises,
    weekHeaderLabels,
    from,
    to,
  };
}

/**
 * @param {Object} params
 * @param {Object[]} params.activeExercises
 * @param {import("moment").Moment|string} params.activeMonth
 * @param {number} [params.startingCalendarDay]
 */
export function buildCalendarMonthInsightsInput({
  activeExercises = [],
  activeMonth,
  startingCalendarDay = 0,
}) {
  const range = getCalendarMonthInsightsRange(activeMonth, startingCalendarDay);
  if (!range.visiblePeriods.length) {
    return null;
  }

  const exercises = getCalendarInsightsExercises(activeExercises, range);

  return buildCalendarRangeInsightsInput({
    exercises,
    ...range,
    startingCalendarDay,
  });
}

/**
 * @param {Object} params
 * @param {Object[]} params.activeExercises
 * @param {import("moment").Moment[]} params.weekStarts
 * @param {number} [params.startingCalendarDay]
 */
export function buildCalendarWeeksInsightsInput({
  activeExercises = [],
  weekStarts,
  startingCalendarDay = 0,
}) {
  const range = getCalendarWeeksInsightsRange(weekStarts, startingCalendarDay);
  if (!range?.visiblePeriods.length) {
    return null;
  }

  const exercises = getCalendarInsightsExercises(activeExercises, range);

  return buildCalendarRangeInsightsInput({
    exercises,
    ...range,
    startingCalendarDay,
  });
}

/**
 * @param {string} athleteId
 * @param {string} from
 * @param {string} to
 */
export function calendarMonthInsightsStorageId(athleteId, from, to) {
  return dateRangeInsightsStorageId("calendar-month", athleteId, from, to);
}

/**
 * @param {string} athleteId
 * @param {string} from
 * @param {string} to
 */
export function calendarWeekInsightsStorageId(athleteId, from, to) {
  return dateRangeInsightsStorageId("calendar-week", athleteId, from, to);
}
